import type { Marker } from "./widgetMapChart";

import { MapPins } from "../../../../constants/mapPins";

type Row = Record<string, unknown>;
const mapTypes: Record<string, "map" | "heatmap"> = {
	SalesMap: "map",
	LeadsMap: "map",
	RepLiveView: "map",
	SalesHeatmap: "heatmap",
	LeadsHeatmap: "heatmap",
	RepLocationHeatmap: "heatmap",
	TurfLeadsHeatmap: "heatmap",
};
const detailFields = [
	"EnrollmentID",
	"EnrollmentStatus",
	"MeterCount",
	"UtilityAccountNumber",
	"Customer",
	"ServiceAddresses",
	"Address",
	"Rep",
	"Machine",
	"Timestamp",
	"CarrierType",
	"Type",
];

function coordinate(value: unknown): number | undefined {
	if (typeof value !== "number" && (typeof value !== "string" || !value.trim())) return undefined;
	const number = Number(value);
	return Number.isFinite(number) ? number : undefined;
}

// The map renders popup labels as HTML; report values must remain text.
function escape(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

function detail(value: unknown): string {
	return escape(Array.isArray(value) ? value.join("; ") : String(value)).replace(/\r?\n/g, "<br />");
}

export function parseWidgetMap(widgetName: string, rows: Row[]): { type: "map" | "heatmap"; markers: Marker[] } | null {
	if (!Object.hasOwn(mapTypes, widgetName)) return null;
	const type = mapTypes[widgetName];
	const markers: Marker[] = [];
	for (const [index, source] of rows.entries()) {
		const row = Object.fromEntries(Object.entries(source).map(([key, value]) => [key.toLowerCase(), value]));
		const lat = coordinate(row.lat),
			lng = coordinate(row.lng);
		if (lat == null || lng == null || lat === 0 || lng === 0 || Math.abs(lat) > 90 || Math.abs(lng) > 180) continue;
		const live = widgetName === "RepLiveView";
		const lead = widgetName === "LeadsMap";
		const identity = live ? row.entityid : lead ? row.id : row.enrollmentid;
		const links: NonNullable<Marker["links"]> = [];
		if (typeof row.enrollmentid === "number" && row.enrollmentid > 0) {
			links.push({ href: `/MVC/Map?enrollmentId=${row.enrollmentid}`, label: "Rep Trail", value: "View" });
		}
		if (
			typeof row.contracturl === "string" &&
			/^\/(?!\/)/.test(row.contracturl) &&
			!row.contracturl.includes("\\")
		) {
			links.push({ href: escape(row.contracturl), label: "Enrollment Documents", value: "View" });
		}
		markers.push({
			id: `${widgetName}:${typeof identity === "number" || typeof identity === "string" ? identity : index}`,
			lat,
			lng,
			ordinal: 0,
			image: live ? MapPins.person : lead ? MapPins.pin : MapPins.pinRed,
			color: lead ? (row.enrollmentid ? "#800080" : "#4b0082") : undefined,
			tooltip: [row.timestamp, live ? undefined : row.rep].filter(Boolean).join(" - "),
			datetime: typeof row.date === "string" ? new Date(row.date) : undefined,
			label: detailFields.flatMap((field) =>
				row[field.toLowerCase()] == null ? [] : [{ [field]: detail(row[field.toLowerCase()]) }],
			),
			links,
		});
	}
	return { type, markers };
}
